/**
 * Mock Report Service
 *
 * In-memory mock that mirrors the real reportService API.
 * Swap this in to test the UI without a backend.
 */

import { mockReports, mockGroups, mockEmails, mockSftpSites } from '../data/mockData';

let reports = JSON.parse(JSON.stringify(mockReports));
let nextId = 100;

const delay = (ms = 200) => new Promise(r => setTimeout(r, ms));

/**
 * Get list of reports with search, filter, and pagination
 */
export const getReports = async (params = {}) => {
  await delay();
  const { search = '', category = '', deliveryMethod = '', sortBy = 'name', sortOrder = 'asc', page = 1, limit = 10 } = params;

  let filtered = reports.filter(r => !r.isArchived);

  if (search) {
    const term = search.toLowerCase();
    filtered = filtered.filter(r =>
      r.name.toLowerCase().includes(term) ||
      r.description.toLowerCase().includes(term)
    );
  }
  if (category) filtered = filtered.filter(r => r.category === category);
  if (deliveryMethod) filtered = filtered.filter(r => r.deliveryMethod === deliveryMethod);

  // Sort
  filtered.sort((a, b) => {
    const aVal = (a[sortBy] || '').toString().toLowerCase();
    const bVal = (b[sortBy] || '').toString().toLowerCase();
    return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  const total = filtered.length;
  const totalPages = Math.ceil(total / limit) || 1;
  const start = (page - 1) * limit;
  const data = filtered.slice(start, start + limit);

  return {
    data,
    pagination: { page, limit, total, totalPages }
  };
};

/**
 * Get report by ID — resolves group/email/sftp IDs to full objects
 */
export const getReportById = async (reportId) => {
  await delay();
  const report = reports.find(r => r.id === reportId);
  if (!report) throw new Error('Report not found');

  return {
    ...report,
    groups: (report.groups || [])
      .map(gId => mockGroups.find(g => g.id === gId))
      .filter(Boolean)
      .map(g => ({ id: g.id, name: g.name, emailCount: g.emailCount })),
    emails: (report.emails || [])
      .map(eId => mockEmails.find(e => e.id === eId))
      .filter(Boolean)
      .map(e => ({ id: e.id, email: e.email, verificationStatus: e.verificationStatus })),
    sftpSites: (report.sftpSites || [])
      .map(sId => mockSftpSites.find(s => s.id === sId))
      .filter(Boolean)
      .map(s => ({ id: s.id, name: s.name }))
  };
};

/**
 * Create new report
 */
export const createReport = async (reportData) => {
  await delay(300);
  const newReport = {
    id: nextId++,
    name: reportData.name,
    description: reportData.description || '',
    category: reportData.category || 'Sales',
    filePath: reportData.filePath || '',
    deliveryMethod: reportData.deliveryMethod || 'SIGNED_URL',
    lastUpdated: new Date().toISOString().split('T')[0],
    createdBy: 'mock@test.com',
    groups: reportData.groupIds || [],
    emails: reportData.emailIds || [],
    sftpSites: reportData.sftpSiteIds || [],
    isArchived: false
  };
  reports.push(newReport);
  return { data: newReport };
};

/**
 * Update report
 */
export const updateReport = async (reportId, reportData) => {
  await delay(300);
  const report = reports.find(r => r.id === reportId);
  if (!report) throw new Error('Report not found');

  report.name = reportData.name;
  report.description = reportData.description;
  report.category = reportData.category;
  report.filePath = reportData.filePath;
  report.deliveryMethod = reportData.deliveryMethod;
  report.groups = reportData.groupIds || [];
  report.emails = reportData.emailIds || [];
  report.sftpSites = reportData.sftpSiteIds || [];
  report.lastUpdated = new Date().toISOString().split('T')[0];

  return { data: report };
};

/**
 * Archive report
 */
export const archiveReport = async (reportId) => {
  await delay(300);
  const report = reports.find(r => r.id === reportId);
  if (!report) throw new Error('Report not found');
  report.isArchived = true;
  return { success: true };
};
